import type { AreaFormRow, FacilityFormRow, UnitFormRow } from "../components/insurance/form/types";

export interface DirtySnapshot {
  areas: string;
  units: string;
  facilities: string;
  imagesIntro: string;
  rounding: string;
}

interface DirtySnapshotArgs {
  areas: AreaFormRow[];
  units: UnitFormRow[];
  facilities: FacilityFormRow[];
  imagesIntro: string;
  roundingAdjustments: { reinstatement: number; inflation: number; demolition: number; drc: number };
}

// Calculated fields are a pure function of the raw inputs, so they are
// excluded from the snapshot to avoid false positives from recalculation.
function stringifyWithoutCalc(value: unknown): string {
  return JSON.stringify(value, (key, v) => (key.startsWith("calc") ? undefined : v));
}

/**
 * Captures the areas/units/facilities/rounding/imagesIntro state so it can be
 * compared later to detect unsaved edits that react-hook-form's isDirty
 * doesn't track (those live outside the form's own field state).
 */
export function snapshotDirtyState(args: DirtySnapshotArgs): DirtySnapshot {
  return {
    areas: stringifyWithoutCalc(args.areas),
    units: stringifyWithoutCalc(args.units),
    facilities: stringifyWithoutCalc(args.facilities),
    imagesIntro: args.imagesIntro,
    rounding: stringifyWithoutCalc(args.roundingAdjustments),
  };
}

export function snapshotsDiffer(baseline: DirtySnapshot | null, current: DirtySnapshot): boolean {
  if (!baseline) return false;
  return (
    baseline.areas !== current.areas ||
    baseline.units !== current.units ||
    baseline.facilities !== current.facilities ||
    baseline.imagesIntro !== current.imagesIntro ||
    baseline.rounding !== current.rounding
  );
}
